import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import '../Navbar.css';
import navbarAdminRoutes from 'router/navbarAdmin';
import NavbarItem from '../NavbarItem';
import { useDispatch, useSelector } from 'react-redux';
import OAuthService from 'services/OAuthService';
import { useDeviceType } from 'components/hooks/useDeviceType';
import { setSidebarItem } from 'redux/slice/sidebarItemSlice';
import Strings from 'values/Strings';

const NavbarAdmin = () => {

  const [links, setLinks] = useState([])
  const listRef = useRef(null)
  const selectorRef = useRef(null)
  const [activeItemRef, setActiveItemRef] = useState(null)
  const sidebarStatus = useSelector(state => state.sidebar.isActive)
  const device = useDeviceType()
  const dispatch = useDispatch()

  // Состояние для управления стрелками навигации
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  // Функции для управления прокруткой
  const scrollLeft = useCallback(() => {
    if (listRef.current) {
      const scrollAmount = 200; // Шаг прокрутки
      listRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  }, []);

  const scrollRight = useCallback(() => {
    if (listRef.current) {
      const scrollAmount = 200; // Шаг прокрутки
      listRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  }, []);

  // Проверка видимости стрелок
  const checkArrowVisibility = useCallback(() => {
    if (listRef.current) {
      const container = listRef.current;
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      // Добавляем небольшой буфер для более точного определения
      const hasOverflow = scrollWidth > clientWidth + 10; // Увеличиваем буфер
      const canScrollLeft = scrollLeft > 5; // Небольшой буфер
      const canScrollRight = scrollLeft < scrollWidth - clientWidth - 5; // Небольшой буфер

      setShowLeftArrow(hasOverflow && canScrollLeft);
      setShowRightArrow(hasOverflow && canScrollRight);
    }
  }, [device, sidebarStatus]);

  const animateSelector = useCallback(() => {
    if (!activeItemRef?.current || !selectorRef.current || !listRef.current) return
    const activeRect = activeItemRef.current.getBoundingClientRect()
    const parentRect = listRef.current.getBoundingClientRect()

    const top = activeRect.top - parentRect.top
    const left = activeRect.left - parentRect.left

    selectorRef.current.style.width = `${activeRect.width}px`
    selectorRef.current.style.height = `${activeRect.height}px`
    selectorRef.current.style.transform = `translate(${left}px, ${top}px)`
    selectorRef.current.style.opacity = '1'
  }, [activeItemRef])

  const handleResize = useCallback(() => {
    requestAnimationFrame(() => {
      animateSelector();
      setTimeout(() => {
        checkArrowVisibility();
        animateSelector();
      }, 100);
      setTimeout(() => {
        checkArrowVisibility();
        animateSelector();
      }, 300);
    });
  }, [animateSelector, checkArrowVisibility]);

  useEffect(() => {
    if (activeItemRef) {
      requestAnimationFrame(() => {
        animateSelector();
      });
    }
  }, [activeItemRef, animateSelector]);

  // Обработка изменения размера окна
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize])

  // Обработка прокрутки навигации
  useEffect(() => {
    const handleScroll = () => {
      checkArrowVisibility();
    };

    const currentListRef = listRef.current;
    if (currentListRef) {
      currentListRef.addEventListener('scroll', handleScroll);

      // Проверяем видимость стрелок с задержкой для корректного рендера
      const timeoutId = setTimeout(() => {
        checkArrowVisibility();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        if (currentListRef) {
          currentListRef.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [checkArrowVisibility])

  // Дополнительная проверка видимости стрелок при изменении ссылок
  useEffect(() => {
    if (links.length > 0) {
      const timeoutId = setTimeout(() => {
        checkArrowVisibility();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [links, checkArrowVisibility])

  useEffect(() => {
    if (device === "desktop" && sidebarStatus !== undefined) {
      const timeoutId = setTimeout(() => {
        checkArrowVisibility();
        animateSelector();
      }, 300);

      const secondTimeoutId = setTimeout(() => {
        checkArrowVisibility();
        animateSelector();
      }, 600);

      return () => {
        clearTimeout(timeoutId);
        clearTimeout(secondTimeoutId);
      };
    }
  }, [sidebarStatus, device, checkArrowVisibility, animateSelector])

  // Проверка видимости стрелок при загрузке компонента
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkArrowVisibility();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [checkArrowVisibility])

  // Дополнительная проверка после полной загрузки
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkArrowVisibility();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [checkArrowVisibility])

  // Обработка изменения размера контейнера навигации (для сайдбара)
  useEffect(() => {
    const handleContainerResize = () => {
      // Проверяем видимость стрелок при изменении размера контейнера
      setTimeout(() => {
        checkArrowVisibility();
      }, 100);
    };

    // Добавляем обработчик изменения размера для контейнера навигации
    if (listRef.current) {
      const resizeObserver = new ResizeObserver(handleContainerResize);
      resizeObserver.observe(listRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [checkArrowVisibility])

  useEffect(() => {
    // Устанавливаем активный элемент только если текущая страница - Админ панель
    const currentPath = window.location.pathname
    if (currentPath.includes('/administration')) {
      dispatch(setSidebarItem(Strings.sidebarAdminPanel))
    }
  }, [dispatch])


  const user = OAuthService.decryptedSync('userData')

  // Мемоизированная фильтрация ссылок
  const filteredLinks = useMemo(() => {
    return navbarAdminRoutes
      .filter(item => {
        if (item.superUser === false) return true;
        if (item.superUser === true && user?.superUser === true) return true;
        return false;
      })
      .map((item, index) => (
        <NavbarItem
          setItemRef={setActiveItemRef}
          key={`${item.name}-${index}`}
          Icon={item.icon}
          title={item.title}
          name={item.name}
        />
      ));
  }, [user?.superUser]);

  useEffect(() => {
    setLinks(filteredLinks);
  }, [filteredLinks]);



  return (
    <nav style={{ width: "100%" }} className="navbar navbar-expand-lg navbar-mainbg">
      <div className="collapse navbar-collapse" id="navbarSupportedContent">
        {/* Кнопка прокрутки влево */}
        <button
          className={`nav-arrow nav-arrow-left ${showLeftArrow ? 'show' : ''}`}
          onClick={scrollLeft}
          aria-label="Прокрутить влево"
        >
          &#8249;
        </button>

        <ul className="navbar-nav ml-auto" ref={listRef}>
          <div className="hori-selector" ref={selectorRef}>
            <div className="left"></div>
            <div className="right"></div>
          </div>
          {links}
        </ul>

        {/* Кнопка прокрутки вправо */}
        <button
          className={`nav-arrow nav-arrow-right ${showRightArrow ? 'show' : ''}`}
          onClick={scrollRight}
          aria-label="Прокрутить вправо"
        >
          &#8250;
        </button>
      </div>
    </nav>
  )
}
export default NavbarAdmin;